import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { DashboardAPI } from '../../components/CustomDashboard/api';
import { singleSelectComponents } from '../../components/CustomDashboard/multiSelectComponents';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ArtifactType, CreateArtifactItem } from '../../utils/artifactsApi';
import { ARTIFACT_TYPE_LABELS, FieldError, getWorkspaceRoles } from '../../utils/artifactsUi';
import { socialGraphApi } from '../../utils/socialGraphApi';
import styles from './ArtifactsPage.module.scss';
import '../../components/CustomDashboard/wf-select-portal.scss';

export interface DraftArtifactItem {
  key: string;
  type: ArtifactType;
  title: string;
  description: string;
  // REPORT: the file to upload; graph / dashboard: the referenced object's id
  file: File | null;
  targetId: string;
}

interface TargetOption {
  value: string;
  label: string;
  // label carries a "(#id)" disambiguator the picker needs but a seeded title should not
  name: string;
}

// The menu portals to document.body, so it has to clear the .modal overlay (z-index 9999)
const selectPortalProps: Record<string, any> = {
  menuPortalTarget: document.body,
  menuPosition: 'fixed' as const,
  styles: { menuPortal: (base: object) => ({ ...base, zIndex: 10000 }) },
};

const PAGE_SIZE = 100;
const MAX_PAGES = 20;

// The list endpoints have no search — walk the pages so the picker offers every target
async function loadAllPages<T>(fetchPage: (page: number) => Promise<{ objects: T[]; total: number }>): Promise<T[]> {
  const all: T[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const { objects, total } = await fetchPage(page);
    all.push(...objects);
    if (objects.length === 0 || all.length >= total) break;
  }
  return all;
}

let draftKeySeq = 0;
const nextKey = () => `draft-${(draftKeySeq += 1)}`;

export function draftFromFile(file: File): DraftArtifactItem {
  return {
    key: nextKey(),
    type: 'REPORT',
    title: file.name.replace(/\.(pdf|html?)$/i, ''),
    description: '',
    file,
    targetId: '',
  };
}

export function draftForRef(type: 'SOCIAL_GRAPH' | 'DASHBOARD'): DraftArtifactItem {
  return { key: nextKey(), type, title: '', description: '', file: null, targetId: '' };
}

// publish-request entry for a draft; the caller pairs REPORT items with their files by order
export function toCreateItem(item: DraftArtifactItem, position: number): CreateArtifactItem {
  const base = { title: item.title.trim(), description: item.description.trim() || null, position };
  if (item.type === 'REPORT') return { ...base, type: 'REPORT' };
  return {
    ...base,
    type: item.type,
    ref:
      item.type === 'SOCIAL_GRAPH'
        ? { kind: 'social_graph', target_id: Number(item.targetId) }
        : { kind: 'custom_dashboard', target_id: item.targetId },
  };
}

export function validateDrafts(items: DraftArtifactItem[], t: (key: string) => string): FieldError | null {
  for (const item of items) {
    if (!item.title.trim()) return { message: t('Every artifact needs a title'), fieldId: `${item.key}-title` };
    if (item.type !== 'REPORT' && !item.targetId.trim()) {
      return { message: t('Pick a target for every graph and dashboard artifact'), fieldId: `${item.key}-target` };
    }
  }
  return null;
}

// Pickers only fetch what the caller may list: the graphs endpoints need the
// social_graph flag and the dashboards ones the menu role — a role-less 403 would
// log the user out. Without the role the target is typed in by id.
function useArtifactTargets(enabled: boolean) {
  const [currentUser] = useCurrentUser();
  const roles = getWorkspaceRoles(currentUser);
  const canListGraphs = Boolean(
    (currentUser as { workspace?: { config?: { featureflags?: string[] } } } | null)?.workspace?.config?.featureflags?.includes(
      'social_graph',
    ),
  );
  const canListDashboards = Boolean(roles?.includes('menu:custom_dashboards'));
  const [graphs, setGraphs] = useState<TargetOption[] | null>(null);
  const [dashboards, setDashboards] = useState<TargetOption[] | null>(null);

  useEffect(() => {
    if (!enabled || !canListGraphs || graphs !== null) return;
    const controller = new AbortController();
    loadAllPages((page) => socialGraphApi.listGraphs({ page, size: PAGE_SIZE }, controller.signal))
      .then((all) =>
        setGraphs(
          all
            .filter((graph) => graph.status === 'ready')
            .map((graph) => ({ value: String(graph.id), label: `${graph.name} (#${graph.id})`, name: graph.name })),
        ),
      )
      .catch(() => setGraphs([]));
    return () => controller.abort();
  }, [enabled, canListGraphs, graphs]);

  useEffect(() => {
    if (!enabled || !canListDashboards || dashboards !== null) return;
    loadAllPages(async (page) => {
      const data = await DashboardAPI.listDashboards(page, PAGE_SIZE);
      return { objects: data.dashboards, total: data.total };
    })
      .then((all) => setDashboards(all.map((d) => ({ value: d.id, label: d.title, name: d.title }))))
      .catch(() => setDashboards([]));
  }, [enabled, canListDashboards, dashboards]);

  return { graphs: canListGraphs ? graphs : undefined, dashboards: canListDashboards ? dashboards : undefined };
}

interface ArtifactItemsEditorProps {
  items: DraftArtifactItem[];
  onChange: (items: DraftArtifactItem[]) => void;
  // single-item mode (add to an existing bundle): one entry, no multi-file add
  single?: boolean;
  disabled?: boolean;
}

export function ArtifactItemsEditor({ items, onChange, single = false, disabled = false }: ArtifactItemsEditorProps) {
  const { t } = useTranslation();
  const targets = useArtifactTargets(items.some((item) => item.type !== 'REPORT') || single);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const update = (key: string, patch: Partial<DraftArtifactItem>) =>
    onChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  const remove = (key: string) => onChange(items.filter((item) => item.key !== key));

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const drafts = Array.from(files).map(draftFromFile);
    onChange(single ? drafts.slice(0, 1) : [...items, ...drafts]);
  };
  const addRef = (type: 'SOCIAL_GRAPH' | 'DASHBOARD') => onChange(single ? [draftForRef(type)] : [...items, draftForRef(type)]);

  const graphOptions = useMemo(() => targets.graphs ?? [], [targets.graphs]);
  const dashboardOptions = useMemo(() => targets.dashboards ?? [], [targets.dashboards]);

  const canAddMore = !single || items.length === 0;

  return (
    <div className={styles.itemsEditor}>
      {items.length === 0 && <p className={styles.itemsEmpty}>{t('No artifacts added yet')}</p>}
      {items.map((item) => {
        const options = item.type === 'SOCIAL_GRAPH' ? targets.graphs : targets.dashboards;
        const optionList = item.type === 'SOCIAL_GRAPH' ? graphOptions : dashboardOptions;
        return (
          <div key={item.key} className={styles.itemRow}>
            <div className={styles.itemRowHeader}>
              <span className={styles.itemType}>{t(ARTIFACT_TYPE_LABELS[item.type])}</span>
              {item.file && <span className={styles.itemFile}>{item.file.name}</span>}
              <button type="button" className={styles.itemRemove} onClick={() => remove(item.key)} disabled={disabled}>
                {t('Remove')}
              </button>
            </div>
            <div className="form-element">
              <label htmlFor={`${item.key}-title`}>{t('Artifact title')}</label>
              <input
                id={`${item.key}-title`}
                type="text"
                value={item.title}
                onChange={(event) => update(item.key, { title: event.target.value })}
                disabled={disabled}
              />
            </div>
            <div className="form-element">
              <label htmlFor={`${item.key}-description`}>{t('Description')}</label>
              <input
                id={`${item.key}-description`}
                type="text"
                value={item.description}
                onChange={(event) => update(item.key, { description: event.target.value })}
                disabled={disabled}
              />
            </div>
            {item.type !== 'REPORT' && (
              <div className="form-element">
                <label htmlFor={`${item.key}-target`}>{t(item.type === 'SOCIAL_GRAPH' ? 'Saved graph' : 'Dashboard')}</label>
                {options !== undefined ? (
                  <Select
                    inputId={`${item.key}-target`}
                    className="wf-select-container"
                    classNamePrefix="wf-select"
                    options={optionList}
                    isLoading={options === null}
                    value={optionList.find((option) => option.value === item.targetId) ?? null}
                    onChange={(selected) => {
                      const option = selected as TargetOption | null;
                      // the picker already knows the name, so seed an untouched title like the report flow does
                      update(item.key, {
                        targetId: option?.value ?? '',
                        ...(option && !item.title.trim() ? { title: option.name } : {}),
                      });
                    }}
                    placeholder={t(item.type === 'SOCIAL_GRAPH' ? 'Choose a saved graph' : 'Choose a dashboard') as string}
                    components={singleSelectComponents}
                    isDisabled={disabled}
                    {...selectPortalProps}
                  />
                ) : (
                  <input
                    id={`${item.key}-target`}
                    type="text"
                    placeholder={t(item.type === 'SOCIAL_GRAPH' ? 'Graph id' : 'Dashboard id') as string}
                    value={item.targetId}
                    onChange={(event) => update(item.key, { targetId: event.target.value.trim() })}
                    disabled={disabled}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      {canAddMore && (
        <div className={styles.itemAdders}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.html,.htm,application/pdf,text/html"
            multiple={!single}
            hidden
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <button type="button" className="btn-primary" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
            {t('Add report files')}
          </button>
          <button type="button" className="btn-primary" onClick={() => addRef('SOCIAL_GRAPH')} disabled={disabled}>
            {t('Add social graph')}
          </button>
          <button type="button" className="btn-primary" onClick={() => addRef('DASHBOARD')} disabled={disabled}>
            {t('Add dashboard')}
          </button>
          <span className={styles.itemsHint}>{t('Only PDF and self-contained HTML files are accepted')}</span>
        </div>
      )}
    </div>
  );
}
